import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle, Eye, EyeOff, Mail, ArrowLeft } from 'lucide-react'
import axios from 'axios'
import { isValidEmail } from '@/lib/utils'

// Brand colors
const BRAND_PINK = '#ff4081'

// Small logo for the form panel - bigger size
const SmallLogo = () => (
  <div className="flex items-center justify-center p-4 rounded-xl border border-gray-200 bg-white shadow-sm w-20 h-20">
    <svg viewBox="0 0 60 60" className="h-12 w-12" fill="none">
      <path d="M30 8 L52 20 L30 32 L8 20 Z" fill="#1e3a5f" />
      <path d="M30 16 L52 28 L30 40 L8 28 Z" fill="#7cb9e8" />
      <path d="M30 24 L52 36 L30 48 L8 36 Z" fill="#ff4081" />
      <path d="M30 32 L52 44 L30 56 L8 44 Z" fill="#4a90c2" />
    </svg>
  </div>
)

// Google Icon
const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
)

// Email Icon
const EmailIcon = () => (
  <Mail className="w-5 h-5 mr-3" />
)

interface OAuthConfig {
  google_enabled: boolean
}

interface OAuthConfigResponse {
  code: number
  success: boolean
  message: string
  data: OAuthConfig
}

type SignupMode = 'choose' | 'email' | 'google'

// Error message mapping
const getErrorMessage = (error: string): string => {
  const errorMessages: Record<string, string> = {
    'email_mismatch': 'The Google account email does not match the email you provided. Please use the same email.',
    'agency_exists': 'An agency with this name already exists. Please choose a different name.',
    'email_exists': 'An account with this email already exists. Please sign in instead.',
    'signup_failed': 'Failed to create your account. Please try again.',
    'oauth_not_configured': 'Google sign-up is not available at this time.',
    'token_exchange_failed': 'Failed to authenticate with Google. Please try again.',
    'failed_to_get_user_info': 'Failed to get your information from Google. Please try again.',
    'invalid_state': 'Your session has expired. Please try again.',
  }
  return errorMessages[error] || 'An error occurred. Please try again.'
}

export default function SignUpPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [oauthConfig, setOauthConfig] = useState<OAuthConfig | null>(null)
  const [mode, setMode] = useState<SignupMode>('choose')

  // Form state for email signup
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    agencyName: '',
    password: '',
    confirmPassword: '',
  })

  // Form state for Google signup
  const [googleFormData, setGoogleFormData] = useState({
    fullName: '',
    email: '',
    agencyName: '',
  })

  // Check for error in URL params (from OAuth callback)
  useEffect(() => {
    const errorParam = searchParams.get('error')
    if (errorParam) {
      setError(getErrorMessage(errorParam))
      // Clear the error from URL
      window.history.replaceState({}, '', '/signup')
    }
  }, [searchParams])

  // Fetch OAuth configuration
  useEffect(() => {
    const fetchOAuthConfig = async () => {
      try {
        const response = await axios.get<OAuthConfigResponse>('/api/auth/oauth/config')
        setOauthConfig(response.data.data)
      } catch {
        // OAuth not available, that's fine
        setOauthConfig({ google_enabled: false })
      }
    }
    fetchOAuthConfig()
  }, [])

  const handleChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value,
    })
  }

  const handleGoogleFormChange = (field: string, value: string) => {
    setGoogleFormData({
      ...googleFormData,
      [field]: value,
    })
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!formData.email || !isValidEmail(formData.email)) {
      setError('Please enter a valid email address')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    setIsLoading(true)

    try {
      await axios.post('/api/auth/signup', {
        agency_name: formData.agencyName,
        full_name: formData.fullName,
        email: formData.email,
        password: formData.password,
      })

      // Redirect to login page with success message
      navigate('/login?signup=success')
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string } } }
        setError(axiosError.response?.data?.message || axiosError.response?.data?.error || 'Failed to create account. Please try again.')
      } else {
        setError('Failed to create account. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!googleFormData.fullName || !googleFormData.agencyName || !googleFormData.email) {
      setError('Please fill in all fields')
      return
    }

    if (!isValidEmail(googleFormData.email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)

    try {
      // Call the Google signup endpoint to get the OAuth URL
      const response = await axios.post<{ data: { oauth_url: string } }>('/api/auth/google/signup', {
        full_name: googleFormData.fullName,
        agency_name: googleFormData.agencyName,
        email: googleFormData.email,
      })

      // Redirect to Google OAuth
      window.location.href = response.data.data.oauth_url
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string } } }
        setError(axiosError.response?.data?.message || axiosError.response?.data?.error || 'Failed to initiate Google sign-up. Please try again.')
      } else {
        setError('Failed to initiate Google sign-up. Please try again.')
      }
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    setMode('choose')
    setError(null)
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12 w-full h-full">
          {/* Main tagline */}
          <div>
            <p className="text-sm font-semibold tracking-widest mb-4" style={{ color: BRAND_PINK }}>
              BLUEPRINT DESK
            </p>
            <h1 className="text-4xl lg:text-5xl font-light text-white leading-tight">
              The <em className="italic" style={{ color: '#94a3b8' }}>operating<br />system</em> for<br />
              <span className="font-semibold">high-growth<br />agencies.</span>
            </h1>
            <div className="w-16 h-1 mt-8" style={{ backgroundColor: BRAND_PINK }} />
          </div>
        </div>
      </div>

      {/* Right Panel - Sign Up Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Small logo */}
          <div className="flex justify-center mb-8">
            <SmallLogo />
          </div>

          {/* Error alert - shown in all modes */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Mode: Choose signup method */}
          {mode === 'choose' && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Create Your Account</h1>
                <p className="text-gray-500">Choose how you'd like to sign up.</p>
              </div>

              <div className="space-y-4">
                {/* Sign up with Email */}
                <Button
                  type="button"
                  className="w-full h-14 text-white rounded-lg font-medium hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)' }}
                  onClick={() => setMode('email')}
                >
                  <EmailIcon />
                  Sign up with Email
                </Button>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-4 text-gray-400 tracking-widest font-medium">
                      Or
                    </span>
                  </div>
                </div>

                {/* Sign up with Google */}
                {oauthConfig?.google_enabled && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-14 rounded-lg border-gray-300 hover:bg-gray-50"
                    onClick={() => setMode('google')}
                  >
                    <GoogleIcon />
                    Sign up with Google
                  </Button>
                )}
              </div>
            </>
          )}

          {/* Mode: Email signup form */}
          {mode === 'email' && (
            <>
              <div className="mb-6">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center text-gray-500 hover:text-gray-700 mb-4"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </button>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign up with Email</h1>
                <p className="text-gray-500">Enter your details to create your account.</p>
              </div>

              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <Input
                  id="fullName"
                  type="text"
                  placeholder="Full Name"
                  value={formData.fullName}
                  onChange={(e) => handleChange('fullName', e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="name"
                  className="h-12 bg-white border border-gray-300 rounded-lg px-4 focus:ring-0 focus:border-gray-400"
                />

                <Input
                  id="agencyName"
                  type="text"
                  placeholder="Agency Name"
                  value={formData.agencyName}
                  onChange={(e) => handleChange('agencyName', e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="organization"
                  className="h-12 bg-white border border-gray-300 rounded-lg px-4 focus:ring-0 focus:border-gray-400"
                />

                <Input
                  id="email"
                  type="email"
                  placeholder="Work Email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="email"
                  className="h-12 bg-white border border-gray-300 rounded-lg px-4 focus:ring-0 focus:border-gray-400"
                />

                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    required
                    disabled={isLoading}
                    autoComplete="new-password"
                    className="h-12 bg-white border border-gray-300 rounded-lg px-4 pr-12 focus:ring-0 focus:border-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    required
                    disabled={isLoading}
                    autoComplete="new-password"
                    className="h-12 bg-white border border-gray-300 rounded-lg px-4 pr-12 focus:ring-0 focus:border-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-white rounded-lg font-medium hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)' }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Mode: Google signup form */}
          {mode === 'google' && (
            <>
              <div className="mb-6">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center text-gray-500 hover:text-gray-700 mb-4"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </button>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign up with Google</h1>
                <p className="text-gray-500">Enter your details, then continue with Google.</p>
              </div>

              <form onSubmit={handleGoogleSubmit} className="space-y-4">
                <Input
                  id="googleFullName"
                  type="text"
                  placeholder="Full Name"
                  value={googleFormData.fullName}
                  onChange={(e) => handleGoogleFormChange('fullName', e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="name"
                  className="h-12 bg-white border border-gray-300 rounded-lg px-4 focus:ring-0 focus:border-gray-400"
                />

                <Input
                  id="googleAgencyName"
                  type="text"
                  placeholder="Agency Name"
                  value={googleFormData.agencyName}
                  onChange={(e) => handleGoogleFormChange('agencyName', e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="organization"
                  className="h-12 bg-white border border-gray-300 rounded-lg px-4 focus:ring-0 focus:border-gray-400"
                />

                <Input
                  id="googleEmail"
                  type="email"
                  placeholder="Google Email (must match your Google account)"
                  value={googleFormData.email}
                  onChange={(e) => handleGoogleFormChange('email', e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="email"
                  className="h-12 bg-white border border-gray-300 rounded-lg px-4 focus:ring-0 focus:border-gray-400"
                />

                <p className="text-sm text-gray-500">
                  Make sure the email above matches your Google account email.
                </p>

                <Button
                  type="submit"
                  variant="outline"
                  className="w-full h-12 rounded-lg border-gray-300 hover:bg-gray-50"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Redirecting to Google...
                    </>
                  ) : (
                    <>
                      <GoogleIcon />
                      Continue with Google
                    </>
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Login link */}
          <div className="mt-8 text-center">
            <span className="text-gray-500">Already have an account?</span>{' '}
            <Link
              to="/login"
              className="font-medium"
              style={{ color: BRAND_PINK }}
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

